import { ComboboxList, TabPanel } from '@ariakit/react';
import { Box, BoxProps, VStack } from '@chakra-ui/react';
import { EmptyState } from '@main/complykit/components/empty-state';
import { LoadingState } from '@main/complykit/components/loading-state';
import { Text } from '@main/complykit/components/text';
import { PropsWithChildren, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { useInfiniteScrollY } from '../hooks';
import { GlobalSearchResult, useGlobalSearchContext } from './global-search-provider';

export function GlobalSearchResultsContainer(props: PropsWithChildren) {
  const globalSearch = useGlobalSearchContext();

  const infiniteScroll = useInfiniteScrollY(globalSearch.onLoadMore);

  return globalSearch.results ? (
    <TabPanel tabId={globalSearch.tabId} style={{ overflow: 'auto' }} {...infiniteScroll}>
      <ComboboxList style={{ width: '100%' }}>{props.children}</ComboboxList>
    </TabPanel>
  ) : null;
}

export interface GlobalSearchResultProps<TResult extends GlobalSearchResult> {
  result: TResult;
}

export type GlobalSearchResultComponent<TResult extends GlobalSearchResult> = (
  props: GlobalSearchResultProps<TResult>,
) => ReactNode;

export interface GlobalSearchResultsProps<TResult extends GlobalSearchResult> extends BoxProps {
  renderResult: GlobalSearchResultComponent<TResult>;
  renderHeader?: ReactNode;
}

export function GlobalSearchResults<TResult extends GlobalSearchResult>({
  renderHeader,
  renderResult,
  ...boxProps
}: GlobalSearchResultsProps<TResult>) {
  const { t } = useTranslation('ui');
  const globalSearch = useGlobalSearchContext<TResult>();
  const ResultComponent = renderResult;

  if (!globalSearch.hasResults) {
    return;
  }

  return (
    <Box w="full" {...boxProps}>
      {renderHeader ?? (
        <Box pb={3}>
          <Text size="sm" weight="medium" color="muted">
            {t('globalSearch.results')}
          </Text>
        </Box>
      )}
      <VStack spacing="1" align="stretch">
        {globalSearch.resultsTab?.map((result, i) => (
          <ResultComponent key={result.id} result={result} />
        ))}
      </VStack>
    </Box>
  );
}

export function GlobalSearchNoResults({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('ui');
  const globalSearch = useGlobalSearchContext();

  if (!globalSearch.isNoResults) {
    return;
  }

  return (
    children ?? (
      <EmptyState
        title={t('globalSearch.noResults.heading')}
        description={t('globalSearch.noResults.description')}
      />
    )
  );
}

// Full loading: replaces content area during tab switch / new search
export function GlobalSearchLoading({ children }: { children?: ReactNode }) {
  const globalSearch = useGlobalSearchContext();
  if (!globalSearch.isSearching || globalSearch.hasResults || globalSearch.isNoResults) {
    return;
  }
  return children ?? <LoadingState />;
}
